import { graphql, useMutation } from 'react-relay';
import { type useFetchAuthToken_forge_ui_userAuthTokenMutation as userAuthTokenMutation } from './__generated__/useFetchAuthToken_forge_ui_userAuthTokenMutation.graphql';
import { useTracingContext } from '../../../error-reporting';
import type { ContextId, ExtensionId } from '@atlassian/forge-ui-types';
import { createAtlAttributionHeader } from '../../../utils';

export interface AuthToken {
	expiry: number; // expiry in ms
	token: string;
}

export const MILLIS_IN_SECOND = 1000;

type FetchAuthToken = (extensionId: ExtensionId, contextIds: ContextId[]) => Promise<AuthToken>;

export const useFetchAuthToken = ({
	renderThreeLOPrompt,
}: {
	renderThreeLOPrompt: (retry: () => Promise<void>) => Promise<void>;
}): FetchAuthToken | undefined => {
	const tracing = useTracingContext();

	const [commitUserAuthTokenForExtensionMutation] = useMutation<userAuthTokenMutation>(graphql`
		mutation useFetchAuthToken_forge_ui_userAuthTokenMutation(
			$input: UserAuthTokenForExtensionInput!
		) {
			userAuthTokenForExtension(input: $input) {
				success
				errors {
					message
				}
				authToken {
					token
					ttl
				}
			}
		}
	`);

	const fetchAuthToken = async (
		extensionId: ExtensionId,
		contextIds: ContextId[],
	): Promise<AuthToken> => {
		tracing?.recordGqlCall('userAuthTokenForExtension - start');

		return new Promise((resolve, reject) => {
			commitUserAuthTokenForExtensionMutation({
				variables: {
					input: {
						extensionId,
						contextIds,
					},
				},
				// @ts-ignore
				cacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
						},
					},
				},
				onCompleted: (data, errors) => {
					if (data?.userAuthTokenForExtension) {
						const { success, authToken } = data.userAuthTokenForExtension;

						tracing?.recordGqlCall('userAuthTokenForExtension - end');

						if (
							!success &&
							errors &&
							errors[0].message.includes(
								'No Atlassian OAuth token found for this user and extension',
							)
						) {
							renderThreeLOPrompt(async () => {
								const token = await fetchAuthToken(extensionId, contextIds);
								resolve(token);
							});
						}

						if (success && authToken) {
							const { token, ttl } = authToken;
							resolve({ token, expiry: Date.now() + ttl * MILLIS_IN_SECOND });
						}
						reject(new Error('An unexpected error occurred when fetching an auth token'));
					}
				},
			});
		});
	};

	return fetchAuthToken;
};
